import React, { useRef } from 'react';
import { BOOKING_VIEW, GROUP_ICON, GROUP_ORDER, GroupKey, IconName, ViewKey } from './groups';
import type { ApartmentEditNavText } from '../../i18n/pages/apartmentEditNav';

export const TAB_PANEL_ID = 'ae-panel';
const tabDomId = (g: GroupKey) => `ae-tab-${g}`;

export interface GroupBadge {
  /** An item of the setup checklist that belongs to this group is not done. */
  attention: boolean;
  /** A field of this group has an edit waiting for Uložit. */
  unsaved: boolean;
}

interface Props {
  /** `null`-like `'booking'` means no group is selected (the URL-only direct-booking view). */
  active: ViewKey;
  badges: Record<GroupKey, GroupBadge>;
  text: ApartmentEditNavText;
  onSelect: (group: GroupKey) => void;
}

/** Lucide/Feather-style line icons, paths copied from the Variant 4 prototype. */
const ICONS: Record<IconName, React.ReactNode> = {
  home: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9v12h14V9" /><path d="M10 21v-6h4v6" /></>,
  calendar: <><rect x="3" y="4.5" width="18" height="16.5" rx="2" /><path d="M8 2.5v4M16 2.5v4M3 10h18" /></>,
  key: <><circle cx="7.5" cy="15.5" r="4.5" /><path d="m10.7 12.3 10-10M16 7l3 3M14 9l2 2" /></>,
  wallet: <><path d="M4 7V6a2 2 0 0 1 2-2h11v3" /><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M16 13.5h2" /></>,
  landmark: <><path d="M3 9.5 12 3l9 6.5z" /><path d="M6 12v6M10 12v6M14 12v6M18 12v6M3 21h18" /></>,
};

export const GroupIcon: React.FC<{ name: IconName }> = ({ name }) => (
  <svg
    viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
  >
    {ICONS[name]}
  </svg>
);

const longLabel = (t: ApartmentEditNavText, g: GroupKey) => ({
  basic: t.groupBasic, reservations: t.groupReservations, arrival: t.groupArrival,
  money: t.groupMoney, authorities: t.groupAuthorities,
}[g]);

const shortLabel = (t: ApartmentEditNavText, g: GroupKey) => ({
  basic: t.shortBasic, reservations: t.shortReservations, arrival: t.shortArrival,
  money: t.shortMoney, authorities: t.shortAuthorities,
}[g]);

export const groupLabel = longLabel;

/**
 * The five group tabs. Desktop: a segmented bar. Up to 640 px (CSS only): one sticky row of five
 * equal columns, icon above a one-word label.
 *
 * <p>The accessible name carries the state ("Peníze — vyžaduje pozornost, neuložené změny") because
 * the badges themselves are purely visual.
 */
const GroupTabs: React.FC<Props> = ({ active, badges, text, onSelect }) => {
  const refs = useRef<Partial<Record<GroupKey, HTMLButtonElement | null>>>({});

  const focusAndSelect = (g: GroupKey) => {
    onSelect(g);
    refs.current[g]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = GROUP_ORDER.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    focusAndSelect(GROUP_ORDER[next]);
  };

  return (
    <div className="ae-tabs" role="tablist" aria-label={text.tabsAria} data-ae-sticky>
      {GROUP_ORDER.map((g, i) => {
        const selected = active === g;
        const b = badges[g];
        const notes = [b.attention ? text.tabNeedsAttention : null, b.unsaved ? text.tabUnsaved : null].filter(Boolean);
        const label = longLabel(text, g);
        const name = notes.length ? `${label} — ${notes.join(', ')}` : label;
        // With nothing selected (booking view) the first tab stays reachable by keyboard.
        const tabbable = selected || (active === BOOKING_VIEW && i === 0);
        return (
          <button
            key={g}
            ref={el => { refs.current[g] = el; }}
            type="button"
            role="tab"
            id={tabDomId(g)}
            className="ae-tab"
            aria-selected={selected}
            aria-controls={selected ? TAB_PANEL_ID : undefined}
            aria-label={name}
            title={label}
            tabIndex={tabbable ? 0 : -1}
            onClick={() => onSelect(g)}
            onKeyDown={e => onKeyDown(e, i)}
          >
            <span className="ae-tab-icon">
              <GroupIcon name={GROUP_ICON[g]} />
              {b.attention && <span className="ae-badge ae-badge-warn" aria-hidden="true" data-testid={`badge-warn-${g}`}>!</span>}
              {b.unsaved && <span className="ae-badge ae-badge-dirty" aria-hidden="true" data-testid={`badge-dirty-${g}`} />}
            </span>
            <span className="ae-tab-label">
              <span className="ae-tab-full" aria-hidden="true">{label}</span>
              <span className="ae-tab-short" aria-hidden="true">{shortLabel(text, g)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default GroupTabs;
export { tabDomId };
